import { Typography } from '@mui/material';
import type { BillingInterval, Currency, UsageProduct, UsageProductInput } from '@weft/types';
import { isEmptyArray } from '@weft/utils';
import { isNullOrUndefined } from '@weft/utils';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { getSectionTitle, getUsageProductsWithIndicesByType } from '../../../../../utils/usage-products-utils';
import { ChargeProvider } from '../charge-context';
import { CreatePlanSection } from '../CreatePlanSection';
import { UsageCharge } from './usage-charges-components/UsageCharge';

export type UsageChargesProps<Product extends UsageProduct | UsageProductInput> = {
  order: Set<'Tiers' | 'PerUnit' | 'Volume' | 'TieredSubscription'>,
  editable: boolean;
  sectionTitle?: string;
  usageProducts: Product[];
  billingInterval: BillingInterval;
  titleClassName?: string;
  currency: Currency
};

export function UsageCharges<Product extends UsageProduct | UsageProductInput>({
  order,
  editable,
  sectionTitle,
  usageProducts,
  billingInterval,
  titleClassName,
  currency,
}: UsageChargesProps<Product>) {
  const usageProductsWithIndicesByType = getUsageProductsWithIndicesByType(usageProducts);

  return (
    <CreatePlanSection
      hideCreateAnother
      title={editable && isNullOrUndefined(sectionTitle) ? undefined : <p className={twMerge('text-base text-[#A6BFC7]', titleClassName)}>{sectionTitle ?? 'Usage Fees'}</p>}
    >
      {
        order.size === 0 ? undefined : (
          <>
            {
              Array.from(order).map((title, index) => (
                <React.Fragment key={title}>
                  {generateChargeProvidersByProduct(
                    {
                      title,
                      editable,
                      currency,
                      billingInterval,
                      products: usageProductsWithIndicesByType[title],
                      showTitle: index !== 0 && editable,
                    })}
                </React.Fragment>
              ))
            }
          </>
        )
      }
    </CreatePlanSection>
  );
}

interface ChargeProviderByProductProps {
  title: 'Tiers' | 'PerUnit' | 'Volume' | 'TieredSubscription'
  editable?: boolean
  currency: Currency
  billingInterval: BillingInterval
  showTitle?: boolean
  products:
  {
    product: UsageProduct | UsageProductInput
    productIndex: number
  }[];
}

function generateChargeProvidersByProduct({ title, editable, currency, billingInterval, products, showTitle }: ChargeProviderByProductProps) {
  if (isEmptyArray(products)) {
    return undefined;
  }

  return (
    <>
      {showTitle ? (
        <Typography className="text-base font-medium">{getSectionTitle(title)}</Typography>
      ) : undefined}

      {products.map(({ product, productIndex }) => (
        <ChargeProvider
          key={`${product.id}-${productIndex}`}
          value={{
            item: product,
            itemIndex: productIndex,
            editable: editable ?? false,
            currency,
          }}
        >
          <UsageCharge
            product={product}
            billingInterval={billingInterval}
            editable={editable ?? false}
          />
        </ChargeProvider>
      ))}
    </>
  );
}
